import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import style from './UserDetails.module.css';
import { uploadingUserDetails, updateUserDetails } from '../../features/userDetailsAction';
import ImageUpload from "../../components/ImageUpload/ImageUpload";
import { imageURLDbUpload, uploadProfileImage } from '../../features/cartAction';
import { showImageStore, resetCart } from '../../features/cartSlice';
import { resetUserDetails } from '../../features/userDetailsSlice';
import { fetchUserSpecificData } from '../../features/auth/authAction';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { DemoContainer, DemoItem } from '@mui/x-date-pickers/internals/demo';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import Slider from '@mui/material/Slider';
import {
    Box,
    Container,
    FormControl,
    Select,
    Card,
    Alert,
    TextField, Button,
    FormLabel,
    MenuItem, Grid, Paper,
    useTheme,
    FormHelperText
} from '@mui/material';
import dayjs from 'dayjs';

export default function BasicGrid() {

    const INIT_USER_DETAILS = {
        userId: '',
        firstName: '',
        lastName: '',
        religion: '',
        gender: '',
        caste: '',
        ethnics: '',
        civilState: '',
        height: '',
        age: '',
        city: '',
        country: '',
        district: '',
        profession: '',
        fEthnics: '',
        fCaste: '',
        fReligion: '',
        fProfession: '',
        fCountry: '',
        mEthnics: '',
        mCaste: '',
        mReligion: '',
        mProfession: '',
        mCountry: '',
        birthday: '',
        birthCountry: '',
        birthCity: '',
        birthTime: '',
        drinking: '',
        smoking: '',
        foodPreference: '',
        phoneNumber: '',
        email: '',
        image: '',
        id: '',
        createdAt: ''
    };

    const dispatch = useDispatch();
    const params = useParams();
    const navigate = useNavigate();
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    const [formDetails, setFormDetails] = useState(INIT_USER_DETAILS);

    const success = useSelector(state => state.details.detailsSuccess);
    const imageStoreData = useSelector(state => state.cartDetails.showImageStore);
    const userSpecificDetails = useSelector(state => state.user?.userSpecificData);
    const loggedUserInfo = useSelector(state => state.auth.loggedUserInfo);
    const currentUserId = params.loggedUId || loggedUserInfo?.user_id;

    useEffect(() => {
        if (currentUserId) {
            dispatch(fetchUserSpecificData(currentUserId));
        }
    }, [dispatch, currentUserId]);

    useEffect(() => {
        if (userSpecificDetails?.userData?.length > 0) {
            const userData = userSpecificDetails.userData[0];

            const updatedForm = { ...INIT_USER_DETAILS, ...userData };

            // --- DATA NORMALIZATION (FIXED) ---

            // 1. Numbers
            if (updatedForm.height) updatedForm.height = Number(updatedForm.height);
            if (updatedForm.age) updatedForm.age = Number(updatedForm.age);

            // 2. Country mappings
            const mapCountry = (c) => {
                if (!c) return '';
                const lower = c.toLowerCase();
                if (lower === 'srilanka' || lower === 'sri lanka' || lower === 'sri-lanka') return 'SriLanka';
                if (lower === 'foreign') return 'foreign';
                if (lower.includes("father") || lower.includes("mother")) return ''; // Handle placeholders
                return c;
            };
            updatedForm.country = mapCountry(updatedForm.country);
            updatedForm.fCountry = mapCountry(updatedForm.fCountry);
            updatedForm.mCountry = mapCountry(updatedForm.mCountry);
            updatedForm.birthCountry = mapCountry(updatedForm.birthCountry);

            // 3. Gender (Title Case)
            if (updatedForm.gender) {
                updatedForm.gender =
                    updatedForm.gender.charAt(0).toUpperCase() +
                    updatedForm.gender.slice(1).toLowerCase();
            }

            // 4. Smoking/Drinking (Yes/No)
            const mapYesNo = (val) => {
                if (val === true || val === 'true' || val?.toLowerCase() === 'yes') return 'Yes';
                if (val === false || val === 'false' || val?.toLowerCase() === 'no') return 'No';
                return '';
            };
            updatedForm.smoking = mapYesNo(updatedForm.smoking);
            updatedForm.drinking = mapYesNo(updatedForm.drinking);

            // 5. Food Preference
            if (updatedForm.foodPreference) {
                const lowerFood = updatedForm.foodPreference.toLowerCase();
                // Map 'vege' to the exact value expected by the Select: "නිර්මාංශ"
                if (lowerFood.includes('vege') && !lowerFood.includes('non')) updatedForm.foodPreference = 'නිර්මාංශ';
                else if (lowerFood.includes('non')) updatedForm.foodPreference = 'මාංශ';
            }

            // 6. Ethnics / Religion / Profession (Sanitization & Mapping)
            const toTitleCase = (str) => {
                if (!str) return '';
                // Catch jargon like "sf" or placeholders
                if (str.length < 3 && str.toLowerCase() !== 'no') return 'Other';
                if (str.toLowerCase().includes("father") || str.toLowerCase().includes("mother")) return '';
                return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
            };

            const professionMap = (p) => {
                if (!p) return '';
                const lower = p.toLowerCase();
                if (lower.includes("father") || lower.includes("mother")) return '';

                // Translate Common English Professions to Sinhala
                if (lower.includes('doctor')) return 'වෛද්යවරයා';
                if (lower.includes('teacher')) return 'ගුරුවරයා';
                if (lower.includes('nurse')) return 'හෙදිය';
                if (lower.includes('lawyer')) return 'නීතිඥයා';
                if (lower.includes('postman')) return 'තැපැල්කරු';
                if (lower.includes('police')) return 'පොලිස් නිලධාරී';
                if (lower.includes('driver')) return 'රියදුරු';
                if (lower.includes('engineer')) return 'ඉංජිනේරු';
                if (lower.includes('clerk')) return 'ලිපිකරු';
                if (lower.includes('bank')) return 'බැංකු සහකාර';
                if (lower.includes('manager')) return 'නිෂ්පාදන කළමනාකරු'; // Simplified map
                if (lower.includes('operator')) return 'ක්රියාකරු';

                return p;
            };

            updatedForm.ethnics = toTitleCase(updatedForm.ethnics);
            updatedForm.profession = professionMap(updatedForm.profession);
            updatedForm.religion = toTitleCase(updatedForm.religion);
            updatedForm.district = toTitleCase(updatedForm.district); // Fix district capitalization

            updatedForm.fEthnics = toTitleCase(updatedForm.fEthnics);
            updatedForm.fReligion = toTitleCase(updatedForm.fReligion);
            updatedForm.mEthnics = toTitleCase(updatedForm.mEthnics);
            updatedForm.mReligion = toTitleCase(updatedForm.mReligion);

            updatedForm.fProfession = professionMap(updatedForm.fProfession);
            updatedForm.mProfession = professionMap(updatedForm.mProfession);

            // Clear caste placeholders
            if (updatedForm.fCaste && updatedForm.fCaste.toLowerCase().includes("father")) updatedForm.fCaste = "";
            if (updatedForm.mCaste && updatedForm.mCaste.toLowerCase().includes("mother")) updatedForm.mCaste = "";


            // 7. Civil State Normalization
            if (updatedForm.civilState === 'Single' || updatedForm.civilState === 'Unmarried') {
                updatedForm.civilState = 'විවාහ වී නැත';
            } else if (updatedForm.civilState === 'Divorced') {
                updatedForm.civilState = 'දික්කසාදයි';
            }

            // Ensure essential IDs are set
            updatedForm.userId = currentUserId;
            updatedForm.id = currentUserId;

            // Cleanup nulls/undefined
            Object.keys(updatedForm).forEach(key => {
                if (updatedForm[key] === null || updatedForm[key] === undefined) {
                    updatedForm[key] = '';
                }
            });

            setFormDetails(updatedForm);
        }
    }, [userSpecificDetails, currentUserId]);

    useEffect(() => {
        if (success === true) {
            if (imageStoreData?.length > 0) {
                imageStoreData.forEach((values) => {
                    dispatch(
                        imageURLDbUpload({
                            userId: currentUserId,
                            imageId: values.url
                        })
                    );
                });
            }
            dispatch(showImageStore("clear"));
            dispatch(resetUserDetails());
            dispatch(resetCart());
            navigate(`/moreDetailsPage/${currentUserId}/${currentUserId}`);
        }
    }, [dispatch, success, navigate, currentUserId, imageStoreData]);

    const handleForm = (event) => {
        if (event && event.target) {
            const value = event.target.value;
            const name = event.target.name;

            setFormDetails(prev => ({
                ...prev,
                [name]: value,
                userId: currentUserId,
                id: currentUserId,
            }));
        }
    };

    const profileImage = useSelector(state => state.details.usersImages);

    const handleFormSubmit = () => {
        // If there is a profile image selected, upload it first
        // If there is a profile image selected (array), upload it first
        if (profileImage && profileImage.length > 0) {
            let formData = new FormData();
            profileImage.forEach((imageItem) => {
                if (imageItem.file) {
                    formData.append("profile_image", imageItem.file);
                }
            });
            formData.append("user_id", params.loggedUId);
            formData.append("isBankSlip", "false");
            formData.append("isProfileImage", "true");
            dispatch(uploadProfileImage(formData));
        }

        // Check if we are in Edit Mode (userSpecificDetails already exists)
        if (userSpecificDetails?.userData?.length > 0) {
            dispatch(updateUserDetails(formDetails));
        } else {
            // Add createdAt to payload for new user details
            const payload = { ...formDetails, createdAt: new Date().toISOString() };
            dispatch(uploadingUserDetails(payload));
        }
    };

    const [errors, setErrors] = useState({});
    const [termsAgreed, setTermsAgreed] = useState(false); // New state for T&C

    const validateForm = () => {
        let tempErrors = {};
        if (!formDetails.firstName) tempErrors.firstName = "First Name is required";
        if (!formDetails.lastName) tempErrors.lastName = "Last Name is required";
        if (!formDetails.gender) tempErrors.gender = "Gender is required";
        if (!formDetails.religion) tempErrors.religion = "Religion is required";
        if (!formDetails.ethnics) tempErrors.ethnics = "Ethnicity is required";
        if (!formDetails.civilState) tempErrors.civilState = "Civil State is required";
        if (!formDetails.height) tempErrors.height = "Height is required";
        if (!formDetails.caste) tempErrors.caste = "Caste is required";
        if (!formDetails.profession) tempErrors.profession = "Profession is required";
        if (!formDetails.city) tempErrors.city = "City is required";
        if (!formDetails.country) tempErrors.country = "Country is required";
        if (!formDetails.district) tempErrors.district = "District is required";

        // Validate Astrology Data
        if (!formDetails.birthday) tempErrors.birthday = "Birthday is required";
        if (!formDetails.birthCountry) tempErrors.birthCountry = "Birth Country is required";
        if (!formDetails.birthCity) tempErrors.birthCity = "Birth City is required";
        if (!formDetails.birthTime) tempErrors.birthTime = "Birth Time is required";

        // Validate Email
        // if (!formDetails.email) {
        //     tempErrors.email = "Email is required";
        // } else if (!/\S+@\S+\.\S+/.test(formDetails.email)) {
        //     tempErrors.email = "Email is invalid";
        // }

        // // Validate Phone
        // if (!formDetails.phoneNumber) {
        //     tempErrors.phoneNumber = "Phone Number is required";
        // }

        setErrors(tempErrors);
        return Object.keys(tempErrors).length === 0;
    };

    const handleSubmitClick = () => {
        if (!termsAgreed) {
            alert("Please agree to the Terms and Conditions to proceed.");
            return;
        }
        if (validateForm()) {
            handleFormSubmit();
        } else {
            // Scroll to the first error
            const errorElement = document.querySelector('.Mui-error');
            if (errorElement) {
                errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }
    };
    return (
        <Box
            sx={{
                flexGrow: 1,
                p: 5,
                backgroundImage: isDark ? 'none' : 'url(../../images/bg-wedding.jpg)',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                backgroundColor: isDark ? 'background.default' : 'transparent',
                boxShadow: isDark ? 'none' : 'inset 0 0 0 1000px rgba(0,0,0,0.20)',
                minHeight: '100vh'
            }} >
            <Container
                sx={{
                    bgcolor: 'background.paper',
                    borderRadius: 2,
                    paddingBottom: 2,
                    maxWidth: 1000,
                    marginBottom: '4rem',
                }}
            >
                <Box
                    sx={{
                        paddingTop: '2rem',
                        paddingLeft: '2rem',
                        fontWeight: 600,
                        fontSize: '20px',
                        paddingBottom: '2rem',
                        color: 'text.primary'
                    }}>
                    මූලික විස්තර {userSpecificDetails?.userData?.length > 0 ? '(Edit Mode)' : ''}
                </Box>
                <Grid container spacing={2}>
                    <Grid item xs={4} md={2} lg={2}>
                        <FormLabel sx={{ color: 'text.primary' }}>මුල් නම</FormLabel>
                    </Grid>
                    <Grid item xs={8} md={4} lg={4}>
                        <TextField
                            name="firstName"
                            value={formDetails.firstName}
                            required
                            id="outlined-required"
                            placeholder='මුල් නම'
                            fullWidth
                            onChange={handleForm}
                            error={!!errors.firstName}
                            helperText={errors.firstName}
                        />
                    </Grid>
                    <Grid item xs={4} md={2} lg={2}>
                        <FormLabel sx={{ color: 'text.primary' }}>අවසන් නම</FormLabel>

                    </Grid>
                    <Grid item xs={8} md={4} lg={4}>
                        <TextField
                            name="lastName"
                            value={formDetails.lastName}
                            required
                            id="outlined-required"
                            placeholder='අවසන් නම'
                            onChange={handleForm}
                            fullWidth
                            error={!!errors.lastName}
                            helperText={errors.lastName}
                        />
                    </Grid>
                    <Grid item xs={4} md={2} lg={2}>
                        <FormLabel sx={{ color: 'text.primary' }}>ආගම</FormLabel>
                    </Grid>
                    <Grid item xs={8} md={4} lg={4}>
                        <FormControl fullWidth error={!!errors.religion}>
                            <Select
                                labelId="demo-simple-select-label"
                                name="religion"
                                required
                                placeholder="ආගම"
                                value={formDetails.religion}
                                onChange={handleForm}
                                displayEmpty
                                renderValue={(v) => v || 'අ තේරීමක් නැත'}
                            >
                                <MenuItem value="Buddhist">බුද්ධාගම</MenuItem>
                                <MenuItem value="Christian">ක්‍රිස්තියානි</MenuItem>
                                <MenuItem value="Islam">ඉස්ලාම්</MenuItem>
                                <MenuItem value="Catholic">කතෝලික</MenuItem>
                                <MenuItem value="None">කිසිවක් නැත</MenuItem>
                                <MenuItem value="Other">වෙනත්</MenuItem>
                            </Select>
                            {errors.religion && <FormHelperText>{errors.religion}</FormHelperText>}
                        </FormControl>
                    </Grid>
                    <Grid item xs={4} md={2} lg={2}>
                        <FormLabel sx={{ color: 'text.primary' }}>ස්ත්‍රී පුරුෂ භාවය</FormLabel>
                    </Grid>
                    <Grid item xs={8} md={4} lg={4}>
                        <FormControl fullWidth error={!!errors.gender}>
                            <Select
                                labelId="demo-simple-select-label"
                                name="gender"
                                required
                                value={formDetails.gender}
                                onChange={handleForm}
                            >
                                <MenuItem value=""><em>අ තේරීමක් නැත</em></MenuItem>
                                <MenuItem value="Male">පිරිමි</MenuItem>
                                <MenuItem value="Female">ගැහැණු</MenuItem>
                            </Select>
                            {errors.gender && <FormHelperText>{errors.gender}</FormHelperText>}
                        </FormControl>
                    </Grid>
                    <Grid item xs={4} md={2} lg={2}>
                        <FormLabel sx={{ color: 'text.primary' }}>ජාතිකත්වය</FormLabel>

                    </Grid>
                    <Grid item xs={8} md={4} lg={4}>
                        <FormControl fullWidth error={!!errors.ethnics}>
                            <Select
                                labelId="demo-simple-select-label"
                                name="ethnics"
                                value={formDetails.ethnics}
                                placeholder='ජාතිකත්වය'
                                onChange={handleForm}
                                required
                            >
                                <MenuItem value=""><em>අ තේරීමක් නැත</em></MenuItem>
                                <MenuItem value="Sinhalese">සිංහල</MenuItem>
                                <MenuItem value="Tamil">දෙමළ</MenuItem>
                                <MenuItem value="Muslim">මුස්ලිම්</MenuItem>
                                <MenuItem value="Burger">බර්ගර්</MenuItem>
                                <MenuItem value="Other">වෙනත්</MenuItem>
                            </Select>
                            {errors.ethnics && <FormHelperText>{errors.ethnics}</FormHelperText>}
                        </FormControl>
                    </Grid>
                    <Grid item xs={4} md={2} lg={2}>
                        <FormLabel sx={{ color: 'text.primary' }}>සිවිල් තත්වය</FormLabel>
                    </Grid>
                    <Grid item xs={8} md={4} lg={4}>
                        <FormControl fullWidth error={!!errors.civilState}>
                            <Select
                                labelId="demo-simple-select-label"
                                name="civilState"
                                value={formDetails.civilState}
                                onChange={handleForm}
                                required
                            >
                                <MenuItem value=""><em>අ තේරීමක් නැත</em></MenuItem>
                                <MenuItem value='විවාහ වී නැත'>අවිවාහක</MenuItem>
                                <MenuItem value='දික්කසාදයි'>දික්කසාදයි</MenuItem>
                                <MenuItem value='වෙනත්'>වෙනත්</MenuItem>

                            </Select>
                            {errors.civilState && <FormHelperText>{errors.civilState}</FormHelperText>}
                        </FormControl>
                    </Grid>
                    <Grid item xs={4} md={2} lg={2}>
                        <FormLabel sx={{ color: 'text.primary' }}>උස</FormLabel>
                    </Grid>
                    <Grid item xs={8} md={4} lg={4}>
                        <FormControl fullWidth error={!!errors.height}>
                            <Select
                                labelId="demo-simple-select-label"
                                name="height"
                                required
                                value={formDetails.height}
                                onChange={handleForm}
                            >
                                <MenuItem value=""><em>අ තේරීමක් නැත</em></MenuItem>
                                <MenuItem value={4}>අඩි 4</MenuItem>
                                <MenuItem value={4.1}>අඩි 4 අඟල් 1</MenuItem>
                                <MenuItem value={4.2}>අඩි 4 අඟල් 2</MenuItem>
                                <MenuItem value={4.3}>අඩි 4 අඟල් 3</MenuItem>
                                <MenuItem value={4.4}>අඩි 4 අඟල් 4</MenuItem>
                                <MenuItem value={4.5}>අඩි 4 අඟල් 5</MenuItem>
                                <MenuItem value={4.6}>අඩි 4 අඟල් 6</MenuItem>
                                <MenuItem value={4.7}>අඩි 4 අඟල් 7</MenuItem>
                                <MenuItem value={4.8}>අඩි 4 අඟල් 8</MenuItem>
                                <MenuItem value={4.9}>අඩි 4 අඟල් 9</MenuItem>
                                <MenuItem value={5}>අඩි 5</MenuItem>
                                <MenuItem value={5.1}>අඩි 5 අඟල් 1</MenuItem>
                                <MenuItem value={5.2}>අඩි 5 අඟල් 2</MenuItem>
                                <MenuItem value={5.3}>අඩි 5 අඟල් 3</MenuItem>
                                <MenuItem value={5.4}>අඩි 5 අඟල් 4</MenuItem>
                                <MenuItem value={5.5}>අඩි 5 අඟල් 5</MenuItem>
                                <MenuItem value={5.6}>අඩි 5 අඟල් 6</MenuItem>
                                <MenuItem value={5.7}>අඩි 5 අඟල් 7</MenuItem>
                                <MenuItem value={5.8}>අඩි 5 අඟල් 8</MenuItem>
                                <MenuItem value={5.9}>අඩි 5 අඟල් 9</MenuItem>
                                <MenuItem value={6}>අඩි 6</MenuItem>
                                <MenuItem value={6.1}>අඩි 6 අඟල් 1</MenuItem>
                                <MenuItem value={6.2}>අඩි 6 අඟල් 2</MenuItem>
                                <MenuItem value={6.3}>අඩි 6 අඟල් 3</MenuItem>
                                <MenuItem value={6.4}>අඩි 6 අඟල් 4</MenuItem>
                                <MenuItem value={6.5}>අඩි 6 අඟල් 5</MenuItem>
                                <MenuItem value={6.6}>අඩි 6 අඟල් 6</MenuItem>
                                <MenuItem value={6.7}>අඩි 6 අඟල් 7</MenuItem>
                                <MenuItem value={6.8}>අඩි 6 අඟල් 8</MenuItem>
                                <MenuItem value={6.9}>අඩි 6 අඟල් 9</MenuItem>
                                <MenuItem value={7}>අඩි 7</MenuItem>
                            </Select>
                            {errors.height && <FormHelperText>{errors.height}</FormHelperText>}
                        </FormControl>
                    </Grid>
                    <Grid item xs={4} md={2} lg={2}>
                        <FormLabel sx={{ color: 'text.primary' }}>ජාතිය</FormLabel>

                    </Grid>
                    <Grid item xs={8} md={4} lg={4}>
                        <TextField
                            name="caste"
                            value={formDetails.caste}
                            required
                            id="outlined-required"
                            placeholder='ජාතිය'
                            onChange={handleForm}
                            fullWidth
                            error={!!errors.caste}
                            helperText={errors.caste}
                        />
                    </Grid>
                    <Grid item xs={4} md={2} lg={2}>
                        <FormLabel sx={{ color: 'text.primary' }}>වෘත්තිය</FormLabel>
                    </Grid>
                    <Grid item xs={8} md={4} lg={4}>
                        <FormControl fullWidth error={!!errors.profession}>
                            <Select
                                labelId="demo-simple-select-label"
                                name="profession"
                                required
                                value={formDetails.profession}
                                onChange={handleForm}
                            >
                                <MenuItem value=""><em>අ තේරීමක් නැත</em></MenuItem>
                                <MenuItem value="වෛද්යවරයා">වෛද්යවරයා</MenuItem>
                                <MenuItem value='ගුරුවරයා'>ගුරුවරයා</MenuItem>
                                <MenuItem value='හෙදිය'>හෙදිය</MenuItem>
                                <MenuItem value='නීතිඥයා'>නීතිඥයා</MenuItem>
                                <MenuItem value='තැපැල්කරු'>තැපැල්කරු</MenuItem>
                                <MenuItem value='පොලිස් නිලධාරී'>පොලිස් නිලධාරී</MenuItem>
                                <MenuItem value='රියදුරු'>රියදුරු</MenuItem>
                                <MenuItem value='ඉංජිනේරු'>ඉංජිනේරු</MenuItem>
                                <MenuItem value='ලිපිකරු'>ලිපිකරු</MenuItem>
                                <MenuItem value='ආහාර බෙදාහරින්නා'>ආහාර බෙදාහරින්නා</MenuItem>
                                <MenuItem value='බැංකු සහකාර'>බැංකු සහකාර</MenuItem>
                                <MenuItem value='නිෂ්පාදන කළමනාකරු'>නිෂ්පාදන කළමනාකරු</MenuItem>
                                <MenuItem value='ක්රියාකරු'>ක්රියාකරු</MenuItem>
                                <MenuItem value='ඇසුරුම් නිලධාරියා'>ඇසුරුම් නිලධාරියා</MenuItem>
                                <MenuItem value='වෙනත්'>වෙනත්</MenuItem>

                            </Select>
                            {errors.profession && <FormHelperText>{errors.profession}</FormHelperText>}
                        </FormControl>
                    </Grid>
                    <Grid item xs={4} md={2} lg={2}>
                        <FormLabel sx={{ color: 'text.primary' }}>Age</FormLabel>
                    </Grid>
                    <Grid item xs={8} md={4} lg={4}>
                        <Slider
                            aria-label="Age"
                            value={formDetails.age ? Number(formDetails.age) : 18}
                            onChange={(_, v) => handleForm({ target: { name: 'age', value: v } })}
                            valueLabelDisplay={formDetails.age ? 'auto' : 'off'}
                            color="primary"
                            name="age"
                            min={18}
                            max={70}
                            sx={{ mt: 1 }}
                        />
                    </Grid>
                </Grid>
            </Container>
            <Container
                sx={{
                    bgcolor: 'background.paper',
                    borderRadius: 2,
                    paddingBottom: 2,
                    maxWidth: 1000,
                    marginBottom: '4rem',
                }}
            >
                <Box
                    sx={{
                        paddingTop: '2rem',
                        paddingLeft: '2rem',
                        fontWeight: 600,
                        fontSize: '20px',
                        paddingBottom: '2rem',
                        color: 'text.primary'
                    }}>
                    පදිංචිය
                </Box>
                <Grid container spacing={2}>
                    <Grid item xs={4} md={2} lg={2}>
                        <FormLabel sx={{ color: 'text.primary' }}>නගරය</FormLabel>

                    </Grid>
                    <Grid item xs={8} md={4} lg={4}>
                        <TextField
                            required
                            id="outlined-required"
                            placeholder='නගරය'
                            name="city"
                            value={formDetails.city}
                            onChange={handleForm}
                            fullWidth
                            error={!!errors.city}
                            helperText={errors.city}
                        />
                    </Grid>
                    <Grid item xs={4} md={2} lg={2}>
                        <FormLabel sx={{ color: 'text.primary' }}>නේවාසික රට</FormLabel>
                    </Grid>
                    <Grid item xs={8} md={4} lg={4}>
                        <FormControl fullWidth error={!!errors.country}>
                            <Select
                                labelId="demo-simple-select-label"
                                name="country"
                                value={formDetails.country}
                                onChange={handleForm}
                                required
                            >
                                <MenuItem value=""><em>අ තේරීමක් නැත</em></MenuItem>
                                <MenuItem value="SriLanka">ශ්‍රී ලංකාව</MenuItem>
                                <MenuItem value="foreign">විදේශීය</MenuItem>
                                <MenuItem value="Other">වෙනත්</MenuItem>

                            </Select>
                            {errors.country && <FormHelperText>{errors.country}</FormHelperText>}
                        </FormControl>
                    </Grid>
                    <Grid item xs={4} md={2} lg={2}>
                        <FormLabel sx={{ color: 'text.primary' }}>ප්‍රාන්තය/ දිස්ත්‍රික්කය</FormLabel>
                    </Grid>
                    <Grid item xs={8} md={4} lg={4}>
                        <FormControl fullWidth error={!!errors.district}>
                            <Select
                                labelId="demo-simple-select-label"
                                name="district"
                                value={formDetails.district}
                                onChange={handleForm}
                                required
                                MenuProps={{
                                    PaperProps: {
                                        style: {
                                            maxHeight: 200,
                                        }
                                    }
                                }}
                            >
                                <MenuItem value=""><em>අ තේරීමක් නැත</em></MenuItem>
                                <MenuItem value="Colombo">කොළඹ</MenuItem>
                                <MenuItem value="Gampaha">ගම්පහ</MenuItem>
                                <MenuItem value="Kandy">මහනුවර</MenuItem>
                                <MenuItem value="Galle">ගාල්ල</MenuItem>
                                <MenuItem value="Matara">මාතර</MenuItem>
                                <MenuItem value="Hambantota">හම්බන්තොට</MenuItem>
                                <MenuItem value="Jaffna">යාපනය</MenuItem>
                                <MenuItem value="Mannar">මන්නාරම</MenuItem>
                                <MenuItem value="Vavuniya">වවුනියාව</MenuItem>
                                <MenuItem value="Mullaitivu">මුලතිව්</MenuItem>
                                <MenuItem value="Kilinochchi">කිලිනොච්චි</MenuItem>
                                <MenuItem value="Batticaloa">මඩකලපුව</MenuItem>
                                <MenuItem value="Ampara">අම්පාර</MenuItem>
                                <MenuItem value="Trincomalee">ත්‍රිකුණාමලය</MenuItem>
                                <MenuItem value="Kurunegala">කුරුණෑගල</MenuItem>
                                <MenuItem value="Puttalam">පුත්තලම</MenuItem>
                                <MenuItem value="Anuradhapura">අනුරාධපුර</MenuItem>
                                <MenuItem value="Polonnaruwa">පොළොන්නරුව</MenuItem>
                                <MenuItem value="Badulla">බදුල්ල</MenuItem>
                                <MenuItem value="Monaragala">මොණරාගල</MenuItem>
                                <MenuItem value="Ratnapura">රත්නපුර</MenuItem>
                                <MenuItem value="Kegalle">කෑගල්ල</MenuItem>
                                <MenuItem value="Nuwara Eliya">නුවරඑළිය</MenuItem>
                                <MenuItem value="Matale">මාතලේ</MenuItem>
                                <MenuItem value="Kalutara">කළුතර</MenuItem>
                                <MenuItem value="Other">වෙනත්</MenuItem>
                            </Select>
                            {errors.district && <FormHelperText>{errors.district}</FormHelperText>}
                        </FormControl>
                    </Grid>
                </Grid>
            </Container>
            <Container
                sx={{
                    bgcolor: 'background.paper',
                    borderRadius: 2,
                    paddingBottom: 2,
                    maxWidth: 1000,
                    marginBottom: '4rem',
                }}
            >
                <Box
                    sx={{
                        paddingTop: '2rem',
                        paddingLeft: '2rem',
                        fontWeight: 600,
                        fontSize: '20px',
                        paddingBottom: '2rem',
                        color: 'text.primary'
                    }}>
                    දෙමව්පියන්ගේ විස්තර (පියා)
                </Box>
                <Grid container spacing={2}>
                    <Grid item xs={4} md={2} lg={2}>
                        <FormLabel sx={{ color: 'text.primary' }}>ජාතිකත්වය</FormLabel>
                    </Grid>
                    <Grid item xs={8} md={4} lg={4}>
                        <Select
                            labelId="demo-simple-select-label"
                            name="fEthnics"
                            value={formDetails.fEthnics}
                            onChange={handleForm}
                            fullWidth
                        >
                            <MenuItem value=""><em>අ තේරීමක් නැත</em></MenuItem>
                            <MenuItem value="Sinhalese">සිංහල</MenuItem>
                            <MenuItem value="Tamil">දෙමළ</MenuItem>
                            <MenuItem value="Muslim">මුස්ලිම්</MenuItem>
                            <MenuItem value="Burger">බර්ගර්</MenuItem>
                            <MenuItem value="Other">වෙනත්</MenuItem>
                        </Select>
                    </Grid>
                    <Grid item xs={4} md={2} lg={2}>
                        <FormLabel sx={{ color: 'text.primary' }}>කුලය</FormLabel>

                    </Grid>
                    <Grid item xs={8} md={4} lg={4}>
                        <TextField
                            id="outlined-required"
                            placeholder='කුලය'
                            name="fCaste"
                            value={formDetails.fCaste}
                            onChange={handleForm}
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={4} md={2} lg={2}>
                        <FormLabel sx={{ color: 'text.primary' }}>ආගම</FormLabel>
                    </Grid>
                    <Grid item xs={8} md={4} lg={4}>
                        <Select
                            labelId="demo-simple-select-label"
                            name="fReligion"
                            value={formDetails.fReligion}
                            onChange={handleForm}
                            fullWidth
                        >
                            <MenuItem value=""><em>අ තේරීමක් නැත</em></MenuItem>
                            <MenuItem value="Buddhist">බුදුදහම</MenuItem>
                            <MenuItem value="Christian">ක්‍රිස්තියානි</MenuItem>
                            <MenuItem value="Islam">ඉස්ලාම්</MenuItem>
                            <MenuItem value="Agnostic">හින්දු</MenuItem>
                            <MenuItem value="Catholic">කතෝලික</MenuItem>
                            <MenuItem value="None">කිසිවක් නැත</MenuItem>
                            <MenuItem value="Other">වෙනත්</MenuItem>
                        </Select>
                    </Grid>
                    <Grid item xs={4} md={2} lg={2}>
                        <FormLabel sx={{ color: 'text.primary' }}>වෘත්තිය</FormLabel>
                    </Grid>
                    <Grid item xs={8} md={4} lg={4}>
                        <Select
                            labelId="demo-simple-select-label"
                            name="fProfession"
                            value={formDetails.fProfession}
                            onChange={handleForm}
                            fullWidth
                        >
                            <MenuItem value=""><em>අ තේරීමක් නැත</em></MenuItem>
                            <MenuItem value="වෛද්යවරයා">වෛද්යවරයා</MenuItem>
                            <MenuItem value='ගුරුවරයා'>ගුරුවරයා</MenuItem>
                            <MenuItem value='හෙදිය'>හෙදිය</MenuItem>
                            <MenuItem value='නීතිඥයා'>නීතිඥයා</MenuItem>
                            <MenuItem value='තැපැල්කරු'>තැපැල්කරු</MenuItem>
                            <MenuItem value='පොලිස් නිලධාරී'>පොලිස් නිලධාරී</MenuItem>
                            <MenuItem value='රියදුරු'>රියදුරු</MenuItem>
                            <MenuItem value='ඉංජිනේරු'>ඉංජිනේරු</MenuItem>
                            <MenuItem value='ලිපිකරු'>ලිපිකරු</MenuItem>
                            <MenuItem value='ආහාර බෙදාහරින්නා'>ආහාර බෙදාහරින්නා</MenuItem>
                            <MenuItem value='බැංකු සහකාර'>බැංකු සහකාර</MenuItem>
                            <MenuItem value='නිෂ්පාදන කළමනාකරු'>නිෂ්පාදන කළමනාකරු</MenuItem>
                            <MenuItem value='ක්රියාකරු'>ක්රියාකරු</MenuItem>
                            <MenuItem value='ඇසුරුම් නිලධාරියා'>ඇසුරුම් නිලධාරියා</MenuItem>
                            <MenuItem value='වෙනත්'>වෙනත්</MenuItem>

                        </Select>
                    </Grid>
                    <Grid item xs={4} md={2} lg={2}>
                        <FormLabel sx={{ color: 'text.primary' }}>නේවාසික රට</FormLabel>
                    </Grid>
                    <Grid item xs={8} md={4} lg={4}>
                        <Select
                            labelId="demo-simple-select-label"
                            name="fCountry"
                            value={formDetails.fCountry}
                            onChange={handleForm}
                            fullWidth
                        >
                            <MenuItem value=""><em>අ තේරීමක් නැත</em></MenuItem>
                            <MenuItem value="SriLanka">ශ්‍රී ලංකාව</MenuItem>
                            <MenuItem value="foreign">විදේශීය</MenuItem>
                            <MenuItem value="Other">වෙනත්</MenuItem>
                        </Select>
                    </Grid>
                </Grid>
            </Container>
            <Container
                sx={{
                    bgcolor: 'background.paper',
                    borderRadius: 2,
                    paddingBottom: 2,
                    maxWidth: 1000,
                    marginBottom: '4rem',
                }}
            >
                <Box
                    sx={{
                        paddingTop: '2rem',
                        paddingLeft: '2rem',
                        fontWeight: 600,
                        fontSize: '20px',
                        paddingBottom: '2rem',
                        color: 'text.primary'
                    }}>
                    දෙමව්පියන්ගේ විස්තර (මව)
                </Box>
                <Grid container spacing={2}>
                    <Grid item xs={4} md={2} lg={2}>
                        <FormLabel sx={{ color: 'text.primary' }}>ජාතිකත්වය</FormLabel>
                    </Grid>
                    <Grid item xs={8} md={4} lg={4}>
                        <Select
                            labelId="demo-simple-select-label"
                            name="mEthnics"
                            value={formDetails.mEthnics}
                            onChange={handleForm}
                            fullWidth
                        >
                            <MenuItem value=""><em>අ තේරීමක් නැත</em></MenuItem>
                            <MenuItem value="Sinhalese">සිංහල</MenuItem>
                            <MenuItem value="Tamil">දෙමළ</MenuItem>
                            <MenuItem value="Muslim">මුස්ලිම්</MenuItem>
                            <MenuItem value="Burger">බර්ගර්</MenuItem>
                            <MenuItem value="Other">වෙනත්</MenuItem>
                        </Select>
                    </Grid>
                    <Grid item xs={4} md={2} lg={2}>
                        <FormLabel sx={{ color: 'text.primary' }}>කුලය</FormLabel>
                    </Grid>
                    <Grid item xs={8} md={4} lg={4}>
                        <TextField
                            id="outlined-required"
                            placeholder='කුලය'
                            name="mCaste"
                            value={formDetails.mCaste}
                            onChange={handleForm}
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={4} md={2} lg={2}>
                        <FormLabel sx={{ color: 'text.primary' }}>ආගම</FormLabel>
                    </Grid>
                    <Grid item xs={8} md={4} lg={4}>
                        <Select
                            labelId="demo-simple-select-label"
                            name="mReligion"
                            value={formDetails.mReligion}
                            onChange={handleForm}
                            fullWidth
                        >
                            <MenuItem value=""><em>අ තේරීමක් නැත</em></MenuItem>
                            <MenuItem value="Buddhist">බුදුදහම</MenuItem>
                            <MenuItem value="Christian">ක්‍රිස්තියානි</MenuItem>
                            <MenuItem value="Islam">ඉස්ලාම්</MenuItem>
                            <MenuItem value="Agnostic">හින්දු</MenuItem>
                            <MenuItem value="Catholic">කතෝලික</MenuItem>
                            <MenuItem value="None">කිසිවක් නැත</MenuItem>
                            <MenuItem value="Other">වෙනත්</MenuItem>
                        </Select>
                    </Grid>
                    <Grid item xs={4} md={2} lg={2}>
                        <FormLabel sx={{ color: 'text.primary' }}>වෘත්තිය</FormLabel>
                    </Grid>
                    <Grid item xs={8} md={4} lg={4}>
                        <Select
                            labelId="demo-simple-select-label"
                            name="mProfession"
                            value={formDetails.mProfession}
                            onChange={handleForm}
                            fullWidth
                        >
                            <MenuItem value=""><em>අ තේරීමක් නැත</em></MenuItem>
                            <MenuItem value="වෛද්යවරයා">වෛද්යවරයා</MenuItem>
                            <MenuItem value='ගුරුවරයා'>ගුරු-වෘත්තිය</MenuItem>
                            <MenuItem value='හෙදිය'>හෙදිය</MenuItem>
                            <MenuItem value='නීතිඥයා'>නීතිඥයා</MenuItem>
                            <MenuItem value='තැපැල්කරු'>තැපැල්කරු</MenuItem>
                            <MenuItem value='පොලිස් නිලධාරී'>පොලිස් නිලධාරී</MenuItem>
                            <MenuItem value='රියදුරු'>රියදුරු</MenuItem>
                            <MenuItem value='ඉංජිනේරු'>ඉංජිනේරු</MenuItem>
                            <MenuItem value='ලිපිකරු'>ලිපිකරු</MenuItem>
                            <MenuItem value='ආහාර බෙදාහරින්නා'>ආහාර බෙදාහරින්නා</MenuItem>
                            <MenuItem value='බැංකු සහකාර'>බැංකු සහකාර</MenuItem>
                            <MenuItem value='නිෂ්පාදන කළමනාකරු'>නිෂ්පාදන කළමනාකරු</MenuItem>
                            <MenuItem value='ක්රියාකරු'>ක්රියාකරු</MenuItem>
                            <MenuItem value='ඇසුරුම් නිලධාරියා'>ඇසුරුම් නිලධාරියා</MenuItem>
                            <MenuItem value='වෙනත්'>වෙනත්</MenuItem>
                        </Select>
                    </Grid>
                    <Grid item xs={4} md={2} lg={2}>
                        <FormLabel sx={{ color: 'text.primary' }}>නේවාසික රට</FormLabel>
                    </Grid>
                    <Grid item xs={8} md={4} lg={4}>
                        <Select
                            labelId="demo-simple-select-label"
                            name="mCountry"
                            value={formDetails.mCountry}
                            onChange={handleForm}
                            fullWidth
                        >
                            <MenuItem value=""><em>අ තේරීමක් නැත</em></MenuItem>
                            <MenuItem value="SriLanka">ශ්‍රී ලංකාව</MenuItem>
                            <MenuItem value="foreign">විදේශීය</MenuItem>
                            <MenuItem value="Other">වෙනත්</MenuItem>
                        </Select>
                    </Grid>
                </Grid>
            </Container>
            <Container
                sx={{
                    bgcolor: 'background.paper',
                    borderRadius: 2,
                    paddingBottom: 2,
                    maxWidth: 1000,
                    marginBottom: '4rem',
                }}
            >
                <Box
                    sx={{
                        paddingTop: '2rem',
                        paddingLeft: '2rem',
                        fontWeight: 600,
                        fontSize: '20px',
                        paddingBottom: '2rem',
                        color: 'text.primary'
                    }}>
                    පුරුදු
                </Box>
                <Grid container spacing={2}>
                    <Grid item xs={4} md={2} lg={2}>
                        <FormLabel sx={{ color: 'text.primary' }}>මත්පැන් පානය</FormLabel>
                    </Grid>
                    <Grid item xs={8} md={4} lg={4}>
                        <Select
                            labelId="demo-simple-select-label"
                            name="drinking"
                            value={formDetails.drinking}
                            onChange={handleForm}
                            fullWidth
                        >
                            <MenuItem value=""><em>අ තේරීමක් නැත</em></MenuItem>
                            <MenuItem value="Yes">ඔව්</MenuItem>
                            <MenuItem value="No">නැහැ</MenuItem>
                        </Select>
                    </Grid>
                    <Grid item xs={4} md={2} lg={2}>
                        <FormLabel sx={{ color: 'text.primary' }}>ආහාර කැමැත්ත</FormLabel>
                    </Grid>
                    <Grid item xs={8} md={4} lg={4}>
                        <Select
                            labelId="demo-simple-select-label"
                            name="foodPreference"
                            value={formDetails.foodPreference}
                            onChange={handleForm}
                            fullWidth
                        >
                            <MenuItem value=""><em>අ තේරීමක් නැත</em></MenuItem>
                            <MenuItem value="මාංශ">මාංශ </MenuItem>
                            <MenuItem value="නිර්මාංශ">නිර්මාංශ</MenuItem>
                        </Select>
                    </Grid>
                    <Grid item xs={4} md={2} lg={2}>
                        <FormLabel sx={{ color: 'text.primary' }}>දුම්පානය</FormLabel>
                    </Grid>
                    <Grid item xs={8} md={4} lg={4}>
                        <Select
                            labelId="demo-simple-select-label"
                            name="smoking"
                            value={formDetails.smoking}
                            onChange={handleForm}
                            fullWidth
                        >
                            <MenuItem value=""><em>අ තේරීමක් නැත</em></MenuItem>
                            <MenuItem value="Yes">ඔව්</MenuItem>
                            <MenuItem value="No">නැහැ</MenuItem>
                        </Select>
                    </Grid>
                </Grid>
            </Container>
            <Container
                sx={{
                    bgcolor: 'background.paper',
                    borderRadius: 2,
                    paddingBottom: 2,
                    maxWidth: 1000,
                    marginBottom: '4rem',
                }}
            >
                <Box
                    sx={{
                        paddingTop: '2rem',
                        paddingLeft: '2rem',
                        fontWeight: 600,
                        fontSize: '20px',
                        paddingBottom: '2rem',
                        color: 'text.primary'
                    }}>
                    ජ්‍යෙතිෂ දත්ත
                </Box>
                <Alert severity="info" sx={{ marginBottom: '3rem', maxHeight: '5%', width: '90%', ml: 3, bgcolor: isDark ? 'rgba(255,255,255,0.1)' : '#EAD9C9', color: isDark ? 'text.primary' : 'inherit' }}>
                    ඔබේ ජ්‍යෙතිෂ දත්ත පෙන්වීමට දෙපාර්ශවයම එකිනෙකාට තම ආකර්ෂණය ලබා දිය යුතුය(එකිනෙකට ආකර්ෂණය දැක්විය යුතුය)
                </Alert>
                <Grid container spacing={2}>
                    <Grid item xs={4} md={2} lg={2}>
                        <FormLabel sx={{ color: 'text.primary' }}>උපන්දිනය</FormLabel>
                    </Grid>
                    <Grid item xs={8} md={4} lg={4}>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DemoContainer components={['DatePicker', 'StaticDatePicker',]}>
                                <DemoItem>
                                    <DatePicker
                                        value={formDetails.birthday ? dayjs(formDetails.birthday) : null}
                                        onChange={(newValue) =>
                                            handleForm({ target: { name: "birthday", value: newValue ? newValue.format('YYYY-MM-DD') : '' } })
                                        }
                                        slotProps={{
                                            textField: {
                                                error: !!errors.birthday,
                                                helperText: errors.birthday
                                            }
                                        }}
                                    />
                                </DemoItem>
                            </DemoContainer>
                        </LocalizationProvider>
                    </Grid>
                    <Grid item xs={4} md={2} lg={2}>
                        <FormLabel sx={{ color: 'text.primary' }}>උපන් රට</FormLabel>
                    </Grid>
                    <Grid item xs={8} md={4} lg={4}>
                        <TextField
                            id="outlined-required"
                            placeholder='උපන් රට'
                            name="birthCountry"
                            value={formDetails.birthCountry}
                            onChange={handleForm}
                            fullWidth
                            error={!!errors.birthCountry}
                            helperText={errors.birthCountry}
                        />
                    </Grid>
                    <Grid item xs={4} md={2} lg={2}>
                        <FormLabel sx={{ color: 'text.primary' }}>උපන් නගරය</FormLabel>
                    </Grid>
                    <Grid item xs={8} md={4} lg={4}>
                        <TextField
                            id="outlined-required"
                            placeholder='උපන් නගරය'
                            name="birthCity"
                            value={formDetails.birthCity}
                            onChange={handleForm}
                            fullWidth
                            error={!!errors.birthCity}
                            helperText={errors.birthCity}
                        />
                    </Grid>
                    <Grid item xs={4} md={2} lg={2}>
                        <FormLabel sx={{ color: 'text.primary' }}>උපන් වේලාව</FormLabel>
                    </Grid>
                    <Grid item xs={8} md={4} lg={4}>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DemoContainer components={['TimePicker', 'StaticTimePicker']}>
                                <DemoItem>
                                    <TimePicker
                                        value={formDetails.birthTime ? dayjs(formDetails.birthTime, 'HH:mm') : null}
                                        onChange={(newValue) =>
                                            handleForm({
                                                target: {
                                                    name: 'birthTime',
                                                    value: newValue ? newValue.format('HH:mm') : '',
                                                },
                                            })
                                        }
                                        slotProps={{
                                            textField: {
                                                error: !!errors.birthTime,
                                                helperText: errors.birthTime
                                            }
                                        }}
                                    />
                                </DemoItem>
                            </DemoContainer>
                        </LocalizationProvider>
                    </Grid>
                    <Alert severity="info" sx={{ m: '2rem', maxHeight: '5%', width: '89%', ml: 4, bgcolor: isDark ? 'rgba(255,255,255,0.1)' : '#EAD9C9', color: isDark ? 'text.primary' : 'inherit' }}>
                        ඔබේ දුරකථන අංකය පින්තූරය පොදු වශයෙන් ප්රදර්ශනය නොවන බව. ඔබේ අංකය පෙන්වීමට දෙපාර්ශවයම එකිනෙකාට තම ආකර්ෂණය ලබා දිය යුතුය

                    </Alert>
                    <Grid item xs={4} md={2} lg={2}>
                        <FormLabel sx={{ color: 'text.primary' }}>දුරකථන අංකය</FormLabel>
                    </Grid>
                    <Grid item xs={8} md={4} lg={4}>
                        <TextField
                            required
                            type="tel"
                            placeholder='+94 7X XXX XXXX'
                            pattern="(\+94|0)[1-9]\d{8}"
                            name="phoneNumber"
                            value={formDetails.phoneNumber}
                            onChange={handleForm}
                            fullWidth
                           
                        />
                    </Grid>
                    <Grid item xs={4} md={2} lg={2}>
                        <FormLabel sx={{ color: 'text.primary' }}>විද්යුත් තැපෑල</FormLabel>
                    </Grid>
                    <Grid item xs={8} md={4} lg={4}>
                        <TextField
                            required
                            type="email"
                            id="outlined-required"
                            placeholder='විද්යුත් තැපෑල'
                            name="email"
                            value={formDetails.email}
                            onChange={handleForm}
                            fullWidth
                        />
                    </Grid>
                    <Alert severity="info" sx={{ m: '2rem', maxHeight: '5%', width: '89%', ml: 4, bgcolor: isDark ? 'rgba(255,255,255,0.1)' : '#EAD9C9', color: isDark ? 'text.primary' : 'inherit' }}>
                        ඔබේ දුරකථන අංකය පින්තූරය පොදු වශයෙන් ප්රදර්ශනය නොවන බව. ඔබේ අංකය පෙන්වීමට දෙපාර්ශවයම එකිනෙකාට තම ආකර්ෂණය ලබා දිය යුතුය

                    </Alert>
                    <Grid item xs={4} md={2} lg={2}>
                        <FormLabel sx={{ color: 'text.primary' }}>ඡායා රූප</FormLabel>
                    </Grid>
                    <Grid item xs={8} md={4} lg={4}>
                        <Box className={style.imageUpload}>
                            <ImageUpload imageType="profileImage" />
                        </Box>
                    </Grid>
                </Grid>
            </Container>

            {/* Terms and Conditions Checkbox */}
            <Container
                sx={{
                    bgcolor: 'background.paper',
                    borderRadius: 2,
                    padding: 2,
                    maxWidth: 1000,
                    marginBottom: '2rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: termsAgreed ? '1px solid transparent' : '1px solid transparent' // Placeholder for validation style
                }}
            >
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <input
                        type="checkbox"
                        id="terms"
                        name="terms"
                        required
                        checked={termsAgreed}
                        style={{
                            marginRight: '10px',
                            transform: 'scale(1.5)',
                            cursor: 'pointer',
                            accentColor: theme.palette.primary.main
                        }}
                        onChange={(e) => setTermsAgreed(e.target.checked)}
                    />
                    <label htmlFor="terms" style={{ color: 'text.primary', fontSize: '16px', cursor: 'pointer' }}>
                        I agree to the <a href="/terms-and-conditions" target="_blank" rel="noopener noreferrer" style={{ color: theme.palette.primary.main, textDecoration: 'underline', fontWeight: 'bold' }}>Terms and Conditions</a> and <a href="/terms-and-conditions" target="_blank" rel="noopener noreferrer" style={{ color: theme.palette.primary.main, textDecoration: 'underline', fontWeight: 'bold' }}>Privacy Policy</a>
                    </label>
                </div>
            </Container >

            <Container sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
                <Button
                    type="submit"
                    form="formDetails"
                    value="submit"
                    variant="contained"
                    color="primary"
                    disabled={!termsAgreed}
                    sx={{
                        m: 4,
                        minWidth: '20%',
                        display: 'flex',
                        alignItems: 'center',
                        borderRadius: 999,
                        opacity: termsAgreed ? 1 : 0.6
                    }}
                    onClick={handleSubmitClick}
                >
                    {userSpecificDetails?.userData?.length > 0 ? ' යාවත්කාලීන කරන්න' : 'ඉදිරිපත් කරන්න'}
                </Button>
            </Container>

        </Box >
    );
}